
import { useState } from 'react'
import { Calendar, User, FileText } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useCreateCalendarAssignment } from '@/api/diet-plans-v2'
import { useUsers } from '@/api/users'
import { UserPicker } from './user-picker'
import type { DietPlanV2 } from '@/types/diet-v2'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'

interface AssignDietPlanDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: DietPlanV2 | null
}

export function AssignDietPlanDialog({ open, onOpenChange, plan }: AssignDietPlanDialogProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [startDate, setStartDate] = useState<Date>()
  const [notes, setNotes] = useState('')

  const { data: users = [] } = useUsers()
  const assignMutation = useCreateCalendarAssignment()

  const handleAssign = async () => {
    if (!plan || !selectedUserId || !startDate) return

    try {
      await assignMutation.mutateAsync({
        userId: selectedUserId,
        dietPlanId: plan._id,
        startDate: format(startDate, 'yyyy-MM-dd'),
        notes,
      })
      onOpenChange(false)
      // Reset form
      setSelectedUserId('')
      setStartDate(undefined)
      setNotes('')
    } catch (error) {
      // Error handled by mutation
    }
  }

  const getDurationLabel = () => {
    if (!plan) return ''
    if (plan.duration === 'day') return '1 day'
    if (plan.duration === 'week') return '7 days'
    return '30 days'
  }

  const calculateEndDate = () => {
    if (!startDate || !plan) return null
    const end = new Date(startDate)
    if (plan.duration === 'week') {
      end.setDate(end.getDate() + 6)
    } else if (plan.duration === 'month') {
      end.setDate(end.getDate() + 29)
    }
    return end
  }

  const endDate = calculateEndDate()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign Diet Plan to User</DialogTitle>
          <DialogDescription>Select a user and start date to assign this diet plan</DialogDescription>
        </DialogHeader>

        {plan && (
          <div className="space-y-6">
            {/* Plan Info */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {plan.name}
              </h4>
              <div className="text-sm space-y-1">
                <div>
                  Duration: <span className="font-medium">{getDurationLabel()}</span>
                </div>
                <div>
                  Avg. Calories: <span className="font-medium">{plan.totalCalories} kcal/day</span>
                </div>
                <div>
                  Macros: <span className="font-medium">P: {plan.macros.protein}g | C: {plan.macros.carbs}g | F: {plan.macros.fat}g</span>
                </div>
              </div>
            </div>

            {/* Select User */}
            <div className="space-y-2">
              <Label htmlFor="user" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Select User *
              </Label>
              <UserPicker
                users={users}
                value={selectedUserId}
                onValueChange={setSelectedUserId}
                placeholder="Choose a user..."
              />
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Start Date *
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('w-full justify-start text-left font-normal', !startDate && 'text-muted-foreground')}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date (calculated) */}
            {endDate && (
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                <div className="text-sm">
                  <strong>Plan will end on:</strong> {format(endDate, 'PPP')}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes for this assignment..."
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedUserId || !startDate || assignMutation.isPending}>
            {assignMutation.isPending ? 'Assigning...' : 'Assign Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
